import React, { useEffect } from "react";
import { View, Text, StyleSheet, Dimensions, Platform } from "react-native";
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../store';
import { clearSuccessMessage } from '../../../store/slices/standUpSlice';
import LhButton from "../../../components/LhButton";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import Svg, { Path } from 'react-native-svg';
import FontFamily from "../../../constants/fonts";

// Props for StandupWelcome component
interface StandupWelcomeProps {
  openFilterOverlay: () => void;
  clearStandup: () => void;
  hideCreateButton?: boolean; // Added prop to control button visibility
}

const StandupWelcome: React.FC<StandupWelcomeProps> = ({
  openFilterOverlay,
  clearStandup,
  hideCreateButton = false, // Default to false
}) => {
  const dispatch = useDispatch();
  
  // Get success message from Redux
  const successMessage = useSelector((state: RootState) => state.standUp.successMessage);

  // Auto-clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        dispatch(clearSuccessMessage());
      }, 5000);

      // Cleanup timer on unmount or when dependencies change
      return () => clearTimeout(timer);
    }
  }, [successMessage, dispatch]);

  // Helper function to check if device is in mobile layout
  const isMobileView = () => {
    const windowWidth = Dimensions.get("window").width;
    return (
      Platform.OS === "ios" || Platform.OS === "android" || windowWidth < 768
    );
  };

  // Helper function to check if device is a very small mobile device
  const isVerySmallMobileView = () => {
    const windowWidth = Dimensions.get("window").width;
    return (
      (Platform.OS === "ios" || Platform.OS === "android") && windowWidth < 375
    );
  };

  // Helper function to check if device is iOS mobile (not iPad or web)
  const isIOSMobile = () => {
    const windowWidth = Dimensions.get("window").width;
    const windowHeight = Dimensions.get("window").height;
    const isSmallScreen = windowWidth < 768 && windowHeight < 1024;
    return Platform.OS === "ios" && isSmallScreen;
  };

  // Determine whether to show button text based on screen size
  const showButtonText = !isVerySmallMobileView();

  return (
    <View style={styles.welcomeCard}>
      {/* Success banner removed to avoid duplicate toasts; global toast (LhToast) is used instead */}
      <View
        style={
          isIOSMobile()
            ? styles.welcomeContainerIOSMobile
            : styles.welcomeContainer
        }
      >
        <View style={styles.welcomeLeftContent}>
          <Text style={styles.welcomeText}>Stand-up</Text>
        </View>
        {!hideCreateButton && ( // Conditionally render the button
          <View
            style={
              isIOSMobile()
                ? styles.beginButtonRowIOSMobile
                : isMobileView()
                ? styles.beginButtonRowMobile
                : styles.beginButtonRow
            }
          >
            <LhButton
              text={showButtonText ? "Create Stand Up" : ""}
              variant="primary"
              size="small"
              onPress={() => {
                console.log("press");
                clearStandup();
                openFilterOverlay();
              }}
              isLoading={false}
              disabled={false}
              style={showButtonText ? styles.beginButton : styles.iconOnlyButton}
              textStyle={
                Platform.OS === "web"
                  ? { fontSize: 16, lineHeight: 22 }
                  : { fontSize: 14, lineHeight: 20 }
              }
            >
              {/* Custom children: edit icon before text */}
              {showButtonText ? (
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <Svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <Path d="M11.06 6.02L11.98 6.94L2.92 16H2V15.08L11.06 6.02ZM14.66 0C14.41 0 14.15 0.1 13.96 0.29L12.13 2.12L15.88 5.87L17.71 4.04C18.1 3.65 18.1 3.02 17.71 2.63L15.37 0.29C15.17 0.09 14.92 0 14.66 0ZM11.06 3.19L0 14.25V18H3.75L14.81 6.94L11.06 3.19Z" fill="#FFFFFF"/>
                  </Svg>
                  <Text numberOfLines={1} ellipsizeMode="tail" style={{ color: '#FFFFFF', fontWeight: '700', fontSize: Platform.OS === 'web' ? 16 : 14, marginLeft: 8 }}>
                    Create Stand-up
                  </Text>
                </View>
              ) : (
                <Svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <Path d="M11.06 6.02L11.98 6.94L2.92 16H2V15.08L11.06 6.02ZM14.66 0C14.41 0 14.15 0.1 13.96 0.29L12.13 2.12L15.88 5.87L17.71 4.04C18.1 3.65 18.1 3.02 17.71 2.63L15.37 0.29C15.17 0.09 14.92 0 14.66 0ZM11.06 3.19L0 14.25V18H3.75L14.81 6.94L11.06 3.19Z" fill="#FFFFFF"/>
                </Svg>
              )}
            </LhButton>
          </View>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  welcomeCard: {},
  welcomeContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",

    padding: 0,
    paddingBottom: 16,
    flexWrap: "nowrap", // Always keep in same row
  },
  welcomeContainerIOSMobile: {
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    padding: 0,
    paddingBottom: 16,
    gap: 12,
  },
  welcomeLeftContent: {
    flexDirection: "row",
    alignItems: "center",
    flexShrink: 1, // Allow this to shrink if needed
  },
  welcomeText: {
    fontFamily: FontFamily.bold,
    fontWeight: "700",
    fontStyle: "normal",
    fontSize: Platform.OS === "web" ? 28 : 20, // h4 size
    lineHeight: Platform.OS === "web" ? 32 : 28, // h4 line height
    letterSpacing: 0,
    color: "#212529",
    flexShrink: 1,
  },
  beginButtonRow: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 10,
    alignItems: "center",
    width: "auto",
    marginTop: 0,
  },
  beginButtonRowMobile: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    width: "auto",
    marginTop: 0,
    gap: 10,
  },
  beginButtonRowIOSMobile: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    width: "100%",
    marginTop: 0,
    gap: 10,
  },
  beginButton: {
    width:
      Dimensions.get("window").width < 400
        ? 90
        : Platform.OS === "web"
        ? 190
        : 120,
    minWidth: 120,
    fontSize: Dimensions.get("window").width < 400 ? 12 : 16,
    backgroundColor: "#004895",
    borderColor: "#034895",
    height: Platform.OS === "web" ? 40 : 32,
    borderRadius: Platform.OS === "web" ? 20 : 24,
    fontFamily: FontFamily.bold,
    fontStyle: "normal",
    fontWeight: "700",
    // Adjusted padding to match design: 8px vertical, 24px horizontal
    paddingVertical: 8,
    paddingHorizontal: 24,
    lineHeight: Platform.OS === "web" ? 22 : 20,
  },
  iconOnlyButton: {
    width: 50,
    minWidth: 50,
    aspectRatio: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#034895",
    borderColor: "#034895",
    height: Platform.OS === "web" ? 40 : 32,
    borderRadius: Platform.OS === "web" ? 20 : 24,
  },
  successMessageContainer: {
    marginBottom: 12,
    width: '100%',
  },
  successMessage: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#d4edda',
    borderColor: '#c3e6cb',
    borderWidth: 1,
    borderRadius: 4,
    padding: 12,
  },
  messageIcon: {
    marginRight: 8,
  },
  successText: {
    flex: 1,
    color: '#155724',
    fontSize: 14,
    fontFamily: FontFamily.regular,
  },
});

export default StandupWelcome;
